import { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, X, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Document guide frame dimensions (mm) and 150PPI target pixels
 * All three are ISO/IEC 7810 ID-1 (credit card size)
 */
const DOC_SPECS = {
  residence_card: { label: '在留カード', w: 85.6, h: 54, color: '#00A651' },
  drivers_license: { label: '運転免許証', w: 85.6, h: 54, color: '#2563eb' },
  my_number_card: { label: 'マイナンバーカード', w: 85.6, h: 54, color: '#7c3aed' },
};

const PPI = 150;

function mmToPixels(mm) {
  return Math.round((mm / 25.4) * PPI);
}

/**
 * Compress image to 150PPI for the document's physical size
 */
export function compressImage(file, docType = 'residence_card') {
  return new Promise((resolve) => {
    const spec = DOC_SPECS[docType] || DOC_SPECS.residence_card;
    const targetW = mmToPixels(spec.w); // ~506px
    const targetH = mmToPixels(spec.h); // ~319px

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');

      // Scale to 150PPI target, keeping aspect ratio
      let w = img.width;
      let h = img.height;
      const maxW = targetW * 2; // Allow 2x for retina, still much smaller than raw
      const maxH = targetH * 2;

      if (w > maxW || h > maxH) {
        const ratio = Math.min(maxW / w, maxH / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }

      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);

      canvas.toBlob(
        (blob) => {
          const compressed = new File([blob], file.name || 'document.jpg', { type: 'image/jpeg' });
          resolve(compressed);
        },
        'image/jpeg',
        0.82
      );
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Full-screen camera with document guide frame overlay
 */
export function DocumentCamera({ docType, side = 'front', onCapture, onClose }) {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const spec = DOC_SPECS[docType] || DOC_SPECS.residence_card;
  const docLabelKeys = {
    residence_card: 'register.docTypeResidence',
    drivers_license: 'register.docTypeLicense',
    my_number_card: 'register.docTypeMyNumber',
  };
  const docLabel = t(docLabelKeys[docType] || 'register.docTypeResidence');

  const [autoMode, setAutoMode] = useState(true);
  const [autoProgress, setAutoProgress] = useState(0);
  const [docPresent, setDocPresent] = useState(false);
  const capturedRef = useRef(false);
  const prevSampleRef = useRef(null);
  const stableCountRef = useRef(0);

  // Compute the crop rectangle (native video px) that matches the on-screen guide frame.
  const computeCropRect = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const vW = video.videoWidth;
    const vH = video.videoHeight;
    const rect = video.getBoundingClientRect();
    const cW = rect.width;
    const cH = rect.height;
    if (!cW || !cH) return null;
    const scale = Math.max(cW / vW, cH / vH);
    const offX = (vW * scale - cW) / 2;
    const offY = (vH * scale - cH) / 2;
    const frameW = 0.85 * cW;
    const frameH = frameW * (spec.h / spec.w);
    const frameLeft = (cW - frameW) / 2;
    const frameTop = (cH - frameH) / 2;
    let sx = (frameLeft + offX) / scale;
    let sy = (frameTop + offY) / scale;
    let sw = frameW / scale;
    let sh = frameH / scale;
    sx = Math.max(0, Math.min(sx, vW));
    sy = Math.max(0, Math.min(sy, vH));
    sw = Math.max(1, Math.min(sw, vW - sx));
    sh = Math.max(1, Math.min(sh, vH - sy));
    return { sx, sy, sw, sh };
  }, [spec.w, spec.h]);

  const startCamera = useCallback(async (facing) => {
    // Stop previous stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => setReady(true);
      }
    } catch (err) {
      console.error('Camera error:', err);
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    startCamera(facingMode);
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, [facingMode, startCamera]);

  const doCapture = useCallback((force = false) => {
    if (capturedRef.current && force !== true) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;
    let r = computeCropRect();
    if (!r) {
      // Fallback: capture the full video frame so the button always works.
      r = { sx: 0, sy: 0, sw: video.videoWidth, sh: video.videoHeight };
    }
    capturedRef.current = true;

    canvas.width = Math.round(r.sw);
    canvas.height = Math.round(r.sh);
    const ctx = canvas.getContext('2d');
    // Crop to the frame: draw only the inside-frame region onto the canvas.
    ctx.drawImage(video, r.sx, r.sy, r.sw, r.sh, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(async (blob) => {
      if (!blob) { capturedRef.current = false; return; }
      const file = new File([blob], 'document-capture.jpg', { type: 'image/jpeg' });
      let compressed;
      try {
        compressed = await compressImage(file, docType);
      } catch (e) {
        compressed = file;
      }
      try {
        const ok = await onCapture(compressed);
        // On success the parent unmounts this component (stream stops on cleanup).
        // On failure keep the camera open so the user can retry.
        if (ok === false) capturedRef.current = false;
      } catch (e) {
        capturedRef.current = false;
      }
    }, 'image/jpeg', 0.95);
  }, [computeCropRect, docType, onCapture]);

  const handleCapture = (force = false) => doCapture(force);

  // Auto capture: when the document is held steady & fills the frame, shoot automatically.
  useEffect(() => {
    if (!ready || !autoMode) { setAutoProgress(0); setDocPresent(false); return; }
    capturedRef.current = false;
    stableCountRef.current = 0;
    prevSampleRef.current = null;
    setAutoProgress(0);
    setDocPresent(false);
    const SAMPLE_W = 48;
    const SAMPLE_H = Math.max(24, Math.round(SAMPLE_W * (spec.h / spec.w)));
    const CONTENT_MIN = 24;       // overall detail/contrast (a real document)
    const EDGE_MIN = 10;          // document edges/text present (not an empty scene)
    const BRIGHT_FRAC_MIN = 0.55; // ID cards are light & should fill the frame
    const COVERAGE_MIN = 0.82;    // detail present across (most of) the frame
    const CELL_STD_MIN = 9;       // per-cell "has detail" threshold
    const SPREAD_MAX = 44;        // low brightness spread => single object fills frame
    const BRIGHT_LO = 95;
    const BRIGHT_HI = 245;
    const MOTION_MAX = 7;         // steady (low frame-to-frame change)
    const STABLE_NEEDED = 4;      // ~0.9s of stability
    const warmupUntil = Date.now() + 900;
    const ac = document.createElement('canvas');
    ac.width = SAMPLE_W; ac.height = SAMPLE_H;
    const actx = ac.getContext('2d', { willReadFrequently: true });
    const id = setInterval(() => {
      if (capturedRef.current) return;
      const video = videoRef.current;
      const r = computeCropRect();
      if (!video || !r) return;
      try {
        actx.drawImage(video, r.sx, r.sy, r.sw, r.sh, 0, 0, SAMPLE_W, SAMPLE_H);
        const data = actx.getImageData(0, 0, SAMPLE_W, SAMPLE_H).data;
        const n = SAMPLE_W * SAMPLE_H;
        const lum = new Float32Array(n);
        let sum = 0;
        for (let i = 0; i < n; i++) {
          const p = i * 4;
          const l = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
          lum[i] = l; sum += l;
        }
        const mean = sum / n;
        let varsum = 0;
        for (let i = 0; i < n; i++) { const d = lum[i] - mean; varsum += d * d; }
        const std = Math.sqrt(varsum / n);
        // Edge density (text/photo/borders present — not an empty/blurred scene).
        let edgeSum = 0, edgeCount = 0;
        for (let y = 0; y < SAMPLE_H; y++) {
          for (let x = 0; x < SAMPLE_W; x++) {
            const idx = y * SAMPLE_W + x;
            if (x + 1 < SAMPLE_W) { edgeSum += Math.abs(lum[idx] - lum[idx + 1]); edgeCount++; }
            if (y + 1 < SAMPLE_H) { edgeSum += Math.abs(lum[idx] - lum[idx + SAMPLE_W]); edgeCount++; }
          }
        }
        const edgeAvg = edgeCount ? edgeSum / edgeCount : 0;

        // Bright fraction: an ID card is light-colored and should fill the frame.
        let brightCount = 0;
        for (let i = 0; i < n; i++) if (lum[i] >= 150) brightCount++;
        const brightFraction = brightCount / n;

        // Grid: coverage (each cell has detail) + brightness uniformity across cells
        // (a single card fills the frame, NOT a card-plus-background mix / partial framing).
        const GRID = 4;
        const cellW = Math.floor(SAMPLE_W / GRID);
        const cellH = Math.floor(SAMPLE_H / GRID);
        let coveredCells = 0;
        const cellMeans = [];
        for (let gy = 0; gy < GRID; gy++) {
          for (let gx = 0; gx < GRID; gx++) {
            let cs = 0, cc = 0;
            for (let y = gy * cellH; y < (gy + 1) * cellH; y++)
              for (let x = gx * cellW; x < (gx + 1) * cellW; x++) { cs += lum[y * SAMPLE_W + x]; cc++; }
            const cm = cc ? cs / cc : 0;
            cellMeans.push(cm);
            let cv = 0;
            for (let y = gy * cellH; y < (gy + 1) * cellH; y++)
              for (let x = gx * cellW; x < (gx + 1) * cellW; x++) { const d = lum[y * SAMPLE_W + x] - cm; cv += d * d; }
            const cstd = cc ? Math.sqrt(cv / cc) : 0;
            if (cstd >= CELL_STD_MIN) coveredCells++;
          }
        }
        const coverage = coveredCells / (GRID * GRID);
        const cmMean = cellMeans.reduce((a, b) => a + b, 0) / cellMeans.length;
        const cmSpread = Math.sqrt(cellMeans.reduce((a, b) => a + (b - cmMean) * (b - cmMean), 0) / cellMeans.length);

        let motion = 999;
        const prev = prevSampleRef.current;
        if (prev) {
          let ms = 0;
          for (let i = 0; i < n; i++) ms += Math.abs(lum[i] - prev[i]);
          motion = ms / n;
        }
        prevSampleRef.current = lum;
        if (Date.now() < warmupUntil) return;
        // STRICT: a light card that FILLS the frame uniformly with visible detail.
        const documentPresent =
          std >= CONTENT_MIN &&
          edgeAvg >= EDGE_MIN &&
          brightFraction >= BRIGHT_FRAC_MIN &&
          coverage >= COVERAGE_MIN &&
          cmSpread <= SPREAD_MAX &&
          mean >= BRIGHT_LO && mean <= BRIGHT_HI;
        setDocPresent(documentPresent);
        const good = documentPresent && motion <= MOTION_MAX;
        stableCountRef.current = good ? stableCountRef.current + 1 : 0;
        setAutoProgress(Math.min(1, stableCountRef.current / STABLE_NEEDED));
        if (stableCountRef.current >= STABLE_NEEDED) {
          clearInterval(id);
          doCapture();
        }
      } catch (e) { /* ignore sampling errors */ }
    }, 220);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, autoMode, facingMode, side, computeCropRect, doCapture]);

  const toggleCamera = () => {
    setFacingMode(f => f === 'environment' ? 'user' : 'environment');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="document-camera">
      {/* Camera feed */}
      <div className="flex-1 relative overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Guide frame overlay */}
        {ready && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 2 }}>

            {/* Card frame - bright inside, dark outside via box-shadow */}
            <div
              className="relative rounded-xl overflow-visible"
              style={{
                width: '85%',
                aspectRatio: `${spec.w}/${spec.h}`,
                border: `2.5px solid ${spec.color}`,
                boxShadow: `0 0 0 9999px rgba(0,0,0,0.65)`,
              }}
            >

              {/* Card layout guide SVG */}
              {side === 'back' ? (
                /* === 裏面ガイド（全書類共通） === */
                <svg viewBox="0 0 856 540" className="absolute inset-0 w-full h-full" style={{ opacity: 0.4 }}>
                  {/* Magnetic stripe */}
                  <rect x="0" y="45" width="856" height="55" fill={spec.color} opacity="0.2" />
                  {/* Text lines */}
                  <rect x="45" y="140" width="760" height="10" rx="2" fill={spec.color} opacity="0.3" />
                  <rect x="45" y="170" width="600" height="10" rx="2" fill={spec.color} opacity="0.3" />
                  <rect x="45" y="200" width="700" height="10" rx="2" fill={spec.color} opacity="0.3" />
                  <rect x="45" y="230" width="500" height="10" rx="2" fill={spec.color} opacity="0.3" />
                  {/* Address / info block */}
                  <rect x="45" y="275" width="760" height="80" rx="5" fill={spec.color} fillOpacity="0.06" stroke={spec.color} strokeWidth="1.5" strokeDasharray="6 3" strokeOpacity="0.2" />
                  <rect x="65" y="295" width="500" height="10" rx="2" fill={spec.color} opacity="0.25" />
                  <rect x="65" y="320" width="400" height="10" rx="2" fill={spec.color} opacity="0.25" />
                  {/* Bottom text lines */}
                  <rect x="45" y="390" width="350" height="10" rx="2" fill={spec.color} opacity="0.3" />
                  <rect x="45" y="420" width="250" height="10" rx="2" fill={spec.color} opacity="0.3" />
                  {/* Barcode / MRZ area */}
                  <rect x="45" y="460" width="760" height="45" rx="4" fill={spec.color} fillOpacity="0.06" stroke={spec.color} strokeWidth="1" strokeOpacity="0.15" />
                  {Array.from({ length: 35 }).map((_, i) => (
                    <rect key={i} x={60 + i * 21} y="468" width={i % 4 === 0 ? 4 : 2} height="30" rx="1" fill={spec.color} opacity="0.25" />
                  ))}
                </svg>
              ) : docType === 'residence_card' ? (
                <svg viewBox="0 0 856 540" className="absolute inset-0 w-full h-full" style={{ opacity: 0.45 }}>
                  {/* Photo area - right side */}
                  <rect x="610" y="60" width="200" height="250" rx="6" fill="none" stroke={spec.color} strokeWidth="3" strokeDasharray="8 4" />
                  <circle cx="710" cy="155" r="45" fill="none" stroke={spec.color} strokeWidth="2" opacity="0.5" />
                  <path d="M680 210 Q710 240 740 210" fill="none" stroke={spec.color} strokeWidth="2" opacity="0.5" />
                  {/* Text lines - left side */}
                  <rect x="45" y="65" width="180" height="12" rx="2" fill={spec.color} opacity="0.5" />
                  <rect x="45" y="95" width="350" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  <rect x="45" y="120" width="300" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  <rect x="45" y="150" width="400" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  <rect x="45" y="175" width="280" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  {/* Status field - center */}
                  <rect x="45" y="220" width="500" height="70" rx="5" fill={spec.color} fillOpacity="0.08" stroke={spec.color} strokeWidth="1.5" strokeOpacity="0.25" />
                  <rect x="65" y="240" width="200" height="12" rx="2" fill={spec.color} opacity="0.3" />
                  <rect x="65" y="265" width="150" height="10" rx="2" fill={spec.color} opacity="0.2" />
                  {/* Bottom lines */}
                  <rect x="45" y="320" width="350" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  <rect x="45" y="345" width="250" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  {/* Barcode area */}
                  <rect x="45" y="400" width="500" height="45" rx="4" fill={spec.color} fillOpacity="0.08" stroke={spec.color} strokeWidth="1" strokeOpacity="0.2" />
                  {[0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25].map(i => (
                    <rect key={i} x={60 + i * 18} y="408" width={i % 3 === 0 ? 4 : 2} height="28" rx="1" fill={spec.color} opacity="0.3" />
                  ))}
                  {/* Card number */}
                  <rect x="45" y="475" width="280" height="12" rx="2" fill={spec.color} opacity="0.3" />
                </svg>
              ) : docType === 'drivers_license' ? (
                <svg viewBox="0 0 856 540" className="absolute inset-0 w-full h-full" style={{ opacity: 0.45 }}>
                  {/* Photo area - left side for license */}
                  <rect x="45" y="100" width="200" height="250" rx="6" fill="none" stroke={spec.color} strokeWidth="3" strokeDasharray="8 4" />
                  <circle cx="145" cy="195" r="45" fill="none" stroke={spec.color} strokeWidth="2" opacity="0.5" />
                  <path d="M115 250 Q145 280 175 250" fill="none" stroke={spec.color} strokeWidth="2" opacity="0.5" />
                  {/* Header */}
                  <rect x="300" y="40" width="250" height="16" rx="3" fill={spec.color} opacity="0.5" />
                  {/* Text lines - right side */}
                  <rect x="300" y="90" width="400" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  <rect x="300" y="115" width="350" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  <rect x="300" y="145" width="450" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  <rect x="300" y="175" width="300" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  <rect x="300" y="205" width="380" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  <rect x="300" y="235" width="250" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  {/* License categories */}
                  <rect x="45" y="390" width="760" height="60" rx="5" fill={spec.color} fillOpacity="0.08" stroke={spec.color} strokeWidth="1.5" strokeOpacity="0.2" />
                  {/* Bottom number */}
                  <rect x="45" y="480" width="350" height="14" rx="2" fill={spec.color} opacity="0.3" />
                </svg>
              ) : (
                <svg viewBox="0 0 856 540" className="absolute inset-0 w-full h-full" style={{ opacity: 0.45 }}>
                  {/* My Number Card - photo right */}
                  <rect x="610" y="80" width="200" height="250" rx="6" fill="none" stroke={spec.color} strokeWidth="3" strokeDasharray="8 4" />
                  <circle cx="710" cy="175" r="45" fill="none" stroke={spec.color} strokeWidth="2" opacity="0.5" />
                  <path d="M680 230 Q710 260 740 230" fill="none" stroke={spec.color} strokeWidth="2" opacity="0.5" />
                  {/* Header */}
                  <rect x="45" y="45" width="300" height="16" rx="3" fill={spec.color} opacity="0.5" />
                  {/* Text lines */}
                  <rect x="45" y="95" width="400" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  <rect x="45" y="125" width="350" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  <rect x="45" y="160" width="450" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  <rect x="45" y="190" width="300" height="10" rx="2" fill={spec.color} opacity="0.35" />
                  {/* Number field */}
                  <rect x="45" y="360" width="500" height="55" rx="5" fill={spec.color} fillOpacity="0.08" stroke={spec.color} strokeWidth="1.5" strokeOpacity="0.2" />
                  <rect x="70" y="378" width="300" height="18" rx="3" fill={spec.color} opacity="0.25" />
                  {/* Bottom */}
                  <rect x="45" y="460" width="280" height="10" rx="2" fill={spec.color} opacity="0.3" />
                  <rect x="45" y="490" width="200" height="10" rx="2" fill={spec.color} opacity="0.3" />
                </svg>
              )}

              {/* Corner markers */}
              {[
                { t: 0, l: 0, br: 'border-t-[3px] border-l-[3px] rounded-tl-xl' },
                { t: 0, r: 0, br: 'border-t-[3px] border-r-[3px] rounded-tr-xl' },
                { b: 0, l: 0, br: 'border-b-[3px] border-l-[3px] rounded-bl-xl' },
                { b: 0, r: 0, br: 'border-b-[3px] border-r-[3px] rounded-br-xl' },
              ].map((c, i) => (
                <div key={i} className={`absolute w-8 h-8 ${c.br}`}
                  style={{ borderColor: spec.color, top: c.t, bottom: c.b, left: c.l, right: c.r }} />
              ))}
            </div>

            {/* Document type label + auto-capture toggle */}
            <div className="absolute top-4 left-0 right-0 flex items-center justify-center gap-2 px-4" style={{ zIndex: 3 }}>
              <span className="px-3 py-1 rounded-full text-white text-sm font-medium"
                style={{ backgroundColor: spec.color }}>
                {docLabel}　{side === 'back' ? t('register.back') : t('register.front')}
              </span>
              <button
                type="button"
                onClick={() => setAutoMode(m => !m)}
                className={`px-3 py-1 rounded-full text-xs font-medium border ${autoMode ? 'bg-white text-black border-white' : 'bg-black/40 text-white border-white/40'}`}
                data-testid="camera-auto-toggle"
              >
                {t('register.camAuto')}: {autoMode ? 'ON' : 'OFF'}
              </button>
            </div>

            {/* Instruction */}
            <div className="absolute bottom-24 left-0 right-0 text-center" style={{ zIndex: 3 }}>
              <p className="text-white text-sm bg-black/50 inline-block px-4 py-1.5 rounded-full">
                {autoMode && docPresent ? t('register.camHoldStill') : t('register.camFrameHint')}
              </p>
            </div>
          </div>
        )}

        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Bottom controls */}
      <div className="bg-black py-6 px-4 flex items-center justify-between safe-area-bottom">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="text-white hover:bg-white/20 w-12 h-12 rounded-full p-0"
          data-testid="camera-close-btn"
        >
          <X className="h-6 w-6" />
        </Button>

        <button
          onClick={() => handleCapture(true)}
          disabled={!ready}
          className="relative w-16 h-16 rounded-full border-4 border-white flex items-center justify-center disabled:opacity-50 active:scale-95 transition-transform"
          data-testid="camera-capture-btn"
        >
          {autoMode && (
            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 64 64" style={{ pointerEvents: 'none' }}>
              <circle cx="32" cy="32" r="30" fill="none" stroke={spec.color} strokeWidth="4"
                strokeDasharray={2 * Math.PI * 30}
                strokeDashoffset={(1 - autoProgress) * 2 * Math.PI * 30}
                strokeLinecap="round" />
            </svg>
          )}
          <div className="w-12 h-12 rounded-full bg-white" />
        </button>

        <Button
          variant="ghost"
          size="sm"
          onClick={toggleCamera}
          className="text-white hover:bg-white/20 w-12 h-12 rounded-full p-0"
          data-testid="camera-flip-btn"
        >
          <RotateCcw className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}
